import React from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
  Legend,
} from "recharts";
import { TID } from "@/lib/testIds";
import { fmt } from "@/lib/format";
import { TrendingUp, Activity } from "lucide-react";

const AXIS = { stroke: "#3f3f46", tick: { fill: "#71717a", fontSize: 11, fontFamily: "JetBrains Mono" } };
const GRID = { stroke: "#27272a", strokeDasharray: "4 4" };

function ChartCard({ title, testid, children, subtitle, right }) {
  return (
    <div className="card-tactical p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3 gap-2">
        <div>
          <div className="text-sm font-semibold text-white">{title}</div>
          {subtitle && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {subtitle}
            </div>
          )}
        </div>
        {right}
      </div>
      <div style={{ width: "100%", height: 220 }}>{children}</div>
    </div>
  );
}

function CustomTooltip({ active, payload, label, unit = "", prefix = "" }) {
  if (!active || !payload || !payload.length) return null;
  const isForecast = payload[0]?.payload?.is_forecast;
  return (
    <div className="bg-zinc-900/95 border border-zinc-700 rounded-md px-3 py-2 shadow-2xl backdrop-blur">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
        <span
          className={`text-[9px] uppercase tracking-widest px-1.5 rounded font-semibold border ${
            isForecast
              ? "bg-blue-500/10 text-blue-300 border-blue-500/30"
              : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
          }`}
        >
          {isForecast ? FORECAST_LABEL : "實際值"}
        </span>
      </div>
      {payload
        .filter((p) => p.value != null)
        .map((p, i) => (
          <div key={i} className="mt-0.5 flex items-center gap-2 font-mono text-xs">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-zinc-400">{p.name || p.dataKey}</span>
            <span className="text-white font-semibold ml-auto">
              {prefix}
              {typeof p.value === "number" ? p.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : p.value}
              {unit}
            </span>
          </div>
        ))}
    </div>
  );
}

const FORECAST_LABEL = "AI 推估";
const FORECAST_TOOLTIP = "AI 推估：以 Finnhub 最近一季 YoY 成長率 ±40% 上限套用季節性外推，非華爾街分析師 consensus。";

const ACTUAL_COLOR = "#10B981";
const FORECAST_COLOR = "#3B82F6";

// Split a quarterly array into two series with nulls so recharts can render
// two adjacent line segments with different styles.
function splitActualForecast(series) {
  return series.map((q, i, arr) => {
    // For continuity, include the last actual point in the forecast series too
    const bridge = !q.is_forecast && arr[i + 1]?.is_forecast;
    return {
      period: q.period,
      is_forecast: q.is_forecast,
      value: q.value,
      actual: q.is_forecast ? null : q.value,
      forecast: q.is_forecast || bridge ? q.value : null,
      prior_estimate: q.prior_estimate ?? null,
      est_high: q.est_high ?? null,
      est_low: q.est_low ?? null,
      // area band range for forecast
      band: q.is_forecast && q.est_low != null && q.est_high != null ? [q.est_low, q.est_high] : null,
    };
  });
}

export default function FinancialCharts({ data }) {
  const fin = data.financials;
  const priceHistory = data.price_history || [];
  const priceStart = priceHistory[0] || 0;
  const priceEnd = priceHistory[priceHistory.length - 1] || 0;
  const up = priceEnd >= priceStart;
  const priceData = priceHistory.map((v, i) => ({ i, price: v }));

  const revData = splitActualForecast(fin.revenue || []);
  const niData = splitActualForecast(fin.net_income || []);
  const epsData = splitActualForecast(fin.eps || []);
  const gmData = splitActualForecast(fin.gross_margin || []);
  const cfData = (fin.operating_cashflow || []).map((r, i) => ({
    period: r.period,
    is_forecast: r.is_forecast,
    operating: r.value,
    free: fin.free_cashflow?.[i]?.value ?? null,
  }));
  const peData = splitActualForecast(fin.pe_history || []);

  return (
    <section className="space-y-4">
      <SectionTitle
        icon={Activity}
        title="財務圖表"
        subtitle="Quarterly · 過去 4 季實際 + 未來 4 季 AI 推估"
      />

      <ForecastLegend />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Price chart */}
        <div className="lg:col-span-8">
          <ChartCard
            title="股價走勢 · 180 日"
            subtitle="Price History"
            testid={TID.chartPriceHistory}
            right={
              <div className="text-right">
                <div className="metric-value text-lg text-white font-bold">
                  {fmt.price(priceEnd, data.currency)}
                </div>
                <div className={`text-xs font-mono ${up ? "text-emerald-400" : "text-red-400"}`}>
                  {up ? "+" : ""}
                  {(((priceEnd - priceStart) / priceStart) * 100).toFixed(2)}%
                </div>
              </div>
            }
          >
            <ResponsiveContainer>
              <AreaChart data={priceData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={up ? "#10B981" : "#EF4444"} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={up ? "#10B981" : "#EF4444"} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID} vertical={false} />
                <XAxis dataKey="i" tick={false} axisLine={false} tickLine={false} />
                <YAxis {...AXIS} width={60} domain={["auto", "auto"]} />
                <Tooltip content={<CustomTooltip prefix="$" />} />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke={up ? "#10B981" : "#EF4444"}
                  strokeWidth={2}
                  fill="url(#priceFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* PE quarterly */}
        <div className="lg:col-span-4">
          <ChartCard title="本益比 P/E" subtitle="Quarterly · 含 AI 推估" testid={TID.chartPe}>
            <ResponsiveContainer>
              <LineChart data={peData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid {...GRID} vertical={false} />
                <XAxis dataKey="period" {...AXIS} />
                <YAxis {...AXIS} width={40} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="實際"
                  stroke="#F59E0B"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#F59E0B", strokeWidth: 0 }}
                />
                <Line
                  type="monotone"
                  dataKey="forecast"
                  name="預估"
                  stroke="#F59E0B"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={{ r: 3, fill: "#F59E0B", strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Revenue quarterly */}
        <div className="lg:col-span-6">
          <ChartCard
            title="營收趨勢 · 季度"
            subtitle="Revenue (B) · Actual + AI Projection"
            testid={TID.chartRevenue}
          >
            <ResponsiveContainer>
              <ComposedChart data={revData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid {...GRID} vertical={false} />
                <XAxis dataKey="period" {...AXIS} />
                <YAxis {...AXIS} width={45} />
                <Tooltip content={<CustomTooltip unit="B" prefix="$" />} />
                <Bar dataKey="value" name="營收" radius={[4, 4, 0, 0]} maxBarSize={44}>
                  {revData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.is_forecast ? FORECAST_COLOR : ACTUAL_COLOR}
                      fillOpacity={entry.is_forecast ? 0.55 : 1}
                      stroke={entry.is_forecast ? FORECAST_COLOR : "transparent"}
                      strokeDasharray={entry.is_forecast ? "3 3" : ""}
                    />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Net Income quarterly */}
        <div className="lg:col-span-6">
          <ChartCard
            title="淨利趨勢 · 季度"
            subtitle="Net Income (B)"
            testid={TID.chartNetIncome}
          >
            <ResponsiveContainer>
              <ComposedChart data={niData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid {...GRID} vertical={false} />
                <XAxis dataKey="period" {...AXIS} />
                <YAxis {...AXIS} width={45} />
                <Tooltip content={<CustomTooltip unit="B" prefix="$" />} />
                <ReferenceLine y={0} stroke="#3f3f46" />
                <Bar dataKey="value" name="淨利" radius={[4, 4, 0, 0]} maxBarSize={44}>
                  {niData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.is_forecast ? FORECAST_COLOR : "#10B981"}
                      fillOpacity={entry.is_forecast ? 0.55 : 1}
                      stroke={entry.is_forecast ? FORECAST_COLOR : "transparent"}
                      strokeDasharray={entry.is_forecast ? "3 3" : ""}
                    />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* EPS */}
        <div className="lg:col-span-4">
          <ChartCard title="EPS · 季度" subtitle="Earnings Per Share" testid={TID.chartEps}>
            <ResponsiveContainer>
              <LineChart data={epsData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid {...GRID} vertical={false} />
                <XAxis dataKey="period" {...AXIS} />
                <YAxis {...AXIS} width={40} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="實際 EPS"
                  stroke="#22D3EE"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#22D3EE", strokeWidth: 0 }}
                />
                <Line
                  type="monotone"
                  dataKey="forecast"
                  name={FORECAST_LABEL}
                  stroke="#22D3EE"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  dot={{ r: 3, fill: "#22D3EE", strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Gross margin */}
        <div className="lg:col-span-4">
          <ChartCard title="毛利率 · 季度" subtitle="Gross Margin %" testid={TID.chartGrossMargin}>
            <ResponsiveContainer>
              <AreaChart data={gmData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="gmFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gmFillFwd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID} vertical={false} />
                <XAxis dataKey="period" {...AXIS} />
                <YAxis {...AXIS} width={48} tickFormatter={(v) => `${v}%`} />
                <Tooltip content={<CustomTooltip unit="%" />} />
                <Area
                  type="monotone"
                  dataKey="actual"
                  name="實際"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  fill="url(#gmFill)"
                />
                <Area
                  type="monotone"
                  dataKey="forecast"
                  name="預估"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  strokeDasharray="5 4"
                  fill="url(#gmFillFwd)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Cash flow */}
        <div className="lg:col-span-4">
          <ChartCard title="現金流 · 季度" subtitle="Operating vs Free CF (B)" testid={TID.chartCashflow}>
            <ResponsiveContainer>
              <BarChart data={cfData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid {...GRID} vertical={false} />
                <XAxis dataKey="period" {...AXIS} />
                <YAxis {...AXIS} width={40} />
                <Tooltip content={<CustomTooltip unit="B" prefix="$" />} />
                <Bar dataKey="operating" name="營運現金流" radius={[3, 3, 0, 0]}>
                  {cfData.map((entry, i) => (
                    <Cell key={i} fill="#10B981" fillOpacity={entry.is_forecast ? 0.5 : 1} />
                  ))}
                </Bar>
                <Bar dataKey="free" name="自由現金流" radius={[3, 3, 0, 0]}>
                  {cfData.map((entry, i) => (
                    <Cell key={i} fill="#22D3EE" fillOpacity={entry.is_forecast ? 0.5 : 1} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Competitor comparison */}
        <div className="lg:col-span-12">
          <ChartCard
            title="與主要競爭對手 · 財務比較"
            subtitle="Peer Comparison · 近期實際值"
            testid={TID.chartCompetitors}
            right={<TrendingUp className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />}
          >
            <ResponsiveContainer>
              <BarChart
                data={buildPeerComparison(data)}
                margin={{ top: 10, right: 10, bottom: 0, left: -20 }}
              >
                <CartesianGrid {...GRID} vertical={false} />
                <XAxis dataKey="name" {...AXIS} />
                <YAxis {...AXIS} width={40} />
                <Tooltip
                  contentStyle={{
                    background: "#18181B",
                    border: "1px solid #3f3f46",
                    borderRadius: 6,
                    fontFamily: "JetBrains Mono",
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#a1a1aa" }}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono", color: "#a1a1aa" }} />
                <Bar dataKey="revenue" name="營收 (B, TTM)" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="margin" name="毛利率 (%)" fill="#10B981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="pe" name="P/E" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </section>
  );
}

function buildPeerComparison(data) {
  const fin = data.financials;
  const rev = fin.revenue || [];
  // Sum last 4 actual quarters (TTM)
  const ttmRev = rev.filter((r) => !r.is_forecast).reduce((a, b) => a + b.value, 0);
  const lastGM = (fin.gross_margin || []).filter((r) => !r.is_forecast).slice(-1)[0]?.value ?? 0;
  return [
    {
      name: data.ticker,
      revenue: Number(ttmRev.toFixed(1)),
      margin: Number(lastGM.toFixed(1)),
      pe: data.pe,
      isSelf: true,
    },
    ...(data.competitors || []).map((c) => ({
      name: c.ticker,
      revenue: c.revenue,
      margin: c.gross_margin,
      pe: c.pe,
      isSelf: false,
    })),
  ];
}

function ForecastLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] font-mono text-zinc-400">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-4 h-3 rounded" style={{ background: ACTUAL_COLOR }} />
        實際值 (Actual)
      </span>
      <span
        className="inline-flex items-center gap-1.5 cursor-help"
        title={FORECAST_TOOLTIP}
        data-testid="forecast-legend-tooltip"
      >
        <span
          className="inline-block w-4 h-3 rounded"
          style={{
            background: FORECAST_COLOR,
            opacity: 0.55,
            border: `1px dashed ${FORECAST_COLOR}`,
          }}
        />
        {FORECAST_LABEL} (AI Projection)
        <span className="text-blue-400/70 text-[10px] font-sans">ⓘ</span>
      </span>
      <span className="text-zinc-500">· 非華爾街分析師 consensus，僅供研究參考</span>
    </div>
  );
}

export function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-baseline gap-3 mb-2">
      <div className="flex items-center gap-2">
        {Icon && (
          <div className="w-7 h-7 rounded-md bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
            <Icon className="w-3.5 h-3.5 text-blue-400" strokeWidth={1.8} />
          </div>
        )}
        <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">{title}</h3>
      </div>
      {subtitle && (
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          {subtitle}
        </span>
      )}
    </div>
  );
}
